// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xGoogleAnalyticsUpdate from "@awc/App/Widgets/GoogleAnalyticsConnector/Handlers/GA4";
import { getGoogleAnalyticsConnectorMapState } from "@awc/App/Widgets/GoogleAnalyticsConnector/Store/Selectors/GoogleAnalyticsSelectors";
import { getDataLayer } from "@awc/App/Widgets/GoogleAnalyticsConnector/Utilities/DataLayer";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const enum fields {
    // Basic Events
    trackGoogleAnalyticsEvents = "trackGoogleAnalyticsEvents",
    addToCartEvent = "addToCartEvent",
    addToCartEventName = "addToCartEventName",
    purchaseEvent = "purchaseEvent",
    purchaseEventName = "purchaseEventName",
    transactionCompleteEventName = "transactionCompleteEventName",
    transactionCompleteEvent = "transactionCompleteEvent",
    removeFromCartLineEvent = "removeFromCartLineEvent",
    removeFromCartLineEventName = "removeFromCartLineEventName",
    pdpViewEvent = "pdpViewEvent",
    pdpViewEventName = "pdpViewEventName",
    productImpressionEvent = "productImpressionEvent",
    productImpressionEventName = "productImpressionEventName",
    selectItemEvent = "selectItemEvent",
    selectItemEventName = "selectItemEventName",
    selectPromotionEvent = "selectPromotionEvent",
    selectPromotionEventName = "selectPromotionEventName",
    searchEvent = "searchEvent",
    searchEventName = "searchEventName",
    noStockSearchEvent = "noStockSearchEvent",
    noStockSearchEventName = "noStockSearchEventName",
    viewCartEvent = "viewCartEvent",
    viewCartEventName = "viewCartEventName",
    // Settings fields
    includeProductBrand = "includeProductBrand",
    includeProductCategory = "includeProductCategory",
    // Custom Events
    loginEvent = "loginEvent",
    loginEventName = "loginEventName",
    setDasboardAsHomepageEvent = "setDasboardAsHomepageEvent",
    setDasboardAsHomepageEventName = "setDasboardAsHomepageEventName",
    returnRequestEvent = "returnRequestEvent",
    returnRequestEventName = "returnRequestEventName",
    contactUsEvent = "contactUsEvent",
    requestQuoteEvent = "requestQuoteEvent",
}

interface OwnProps extends WidgetProps {
    fields: {
        // Basic Events
        [fields.trackGoogleAnalyticsEvents]: boolean;
        [fields.addToCartEvent]: boolean;
        [fields.addToCartEventName]: string;
        [fields.purchaseEvent]: boolean;
        [fields.purchaseEventName]: string;
        [fields.removeFromCartLineEvent]: boolean;
        [fields.removeFromCartLineEventName]: string;
        [fields.pdpViewEvent]: boolean;
        [fields.pdpViewEventName]: string;
        [fields.productImpressionEvent]: boolean;
        [fields.productImpressionEventName]: string;
        [fields.selectItemEvent]: boolean;
        [fields.selectItemEventName]: string;
        [fields.selectPromotionEvent]: boolean;
        [fields.selectPromotionEventName]: string;
        [fields.searchEvent]: boolean;
        [fields.searchEventName]: string;
        [fields.noStockSearchEvent]: boolean;
        [fields.noStockSearchEventName]: string;
        [fields.viewCartEvent]: boolean;
        [fields.viewCartEventName]: string;
        // Settings Fields
        [fields.includeProductBrand]: boolean;
        [fields.includeProductCategory]: boolean;
        // Custom Events
        [fields.loginEvent]: boolean;
        [fields.loginEventName]: string;
        [fields.transactionCompleteEvent]: boolean;
        [fields.transactionCompleteEventName]: string;
        [fields.setDasboardAsHomepageEvent]: boolean;
        [fields.setDasboardAsHomepageEventName]: string;
        [fields.returnRequestEvent]: boolean;
        [fields.returnRequestEventName]: string;
        [fields.contactUsEvent]: boolean;
        [fields.requestQuoteEvent]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        ...getGoogleAnalyticsConnectorMapState(state),
    };
};

const mapDispatchToProps = {
    xGoogleAnalyticsUpdate,
    resetShouldTrackPayment: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackPayment",
            shouldTrackPayment: false,
        } as any);
    },
    resetShouldTrackBeginCheckout: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackBeginOnePageCheckout",
            shouldTrackBeginOnePageCheckout: false,
        } as any);
    },
    resetShouldTrackNoStockSearch: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackNoStockSearch",
            shouldTrackNoStockSearch: false,
        } as any);
    },
    setAfilliation: (affiliation?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setAffiliation",
            affiliation,
        } as any);
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface GoogleAnalyticsConnectorStyles {
    container?: InjectableCss;
}

const googleAnalyticsConnectorStyles: GoogleAnalyticsConnectorStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = googleAnalyticsConnectorStyles;
const GoogleAnalyticsConnector: FC<Props> = ({
    xGoogleAnalyticsUpdate,
    fields,
    cart,
    isCartPage,
    xGoogleAnalytics,
    currentPage,
    currencyCode,
    currencySymbol,
    affiliation,
    locationId,
    isPLP,
    isPDP,
    PLPProducts,
    PDPProduct,
    resetShouldTrackPayment,
    resetShouldTrackBeginCheckout,
    resetShouldTrackNoStockSearch,
    userEmail,
    query,
    setAfilliation,
}) => {
    const { isInShell } = useShellContext();

    const [PDPInfoLoaded, setPDPInfoLoaded] = useState<boolean>(false);
    const [PLPInfoLoaded, setPLPInfoLoaded] = useState<boolean>(false);

    // Placeholder in CMS view
    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="GoogleAnalyticsConnector">
                <Typography>{translate("Google Analytics Connector Widget")}</Typography>
            </StyledWrapper>
        );
    }

    useEffect(() => {
        if (!fields.trackGoogleAnalyticsEvents) {
            return;
        }

        xGoogleAnalyticsUpdate({
            trackGoogleAnalytics: true,
            addToCartEvent: fields["addToCartEvent"] || false,
            addToCartEventName: fields["addToCartEventName"] || "add_to_cart",
            purchaseEvent: fields["purchaseEvent"] || false,
            purchaseEventName: fields["purchaseEventName"] || "purchase",
            transactionCompleteEventName: fields["transactionCompleteEventName"] || "transactionComplete",
            transactionCompleteEvent: fields["transactionCompleteEvent"] || false,
            removeFromCartLineEvent: fields["removeFromCartLineEvent"] || false,
            removeFromCartLineEventName: fields["removeFromCartLineEventName"] || "remove_from_cart",
            pdpViewEvent: fields["pdpViewEvent"] || false,
            pdpViewEventName: fields["pdpViewEventName"] || "view_item",
            productImpressionEvent: fields["productImpressionEvent"] || false,
            productImpressionEventName: fields["productImpressionEventName"] || "productImpression",
            selectItemEvent: fields["selectItemEvent"] || false,
            selectItemEventName: fields["selectItemEventName"] || "select_item",
            selectPromotionEvent: fields["selectPromotionEvent"] || false,
            selectPromotionEventName: fields["selectPromotionEventName"] || "selectPromotionEventName",
            searchEvent: fields["searchEvent"] || false,
            searchEventName: fields["searchEventName"] || "search",
            noStockSearchEvent: fields["noStockSearchEvent"] || false,
            noStockSearchEventName: fields["noStockSearchEventName"] || "nostock_search",
            viewCartEvent: fields["viewCartEvent"] || false,
            viewCartEventName: fields["viewCartEventName"] || "view_cart",
            includeProductBrand: fields["includeProductBrand"] || false,
            includeProductCategory: fields["includeProductCategory"] || false,
            loginEvent: fields["loginEvent"] || false,
            loginEventName: fields["loginEventName"] || "login",
            setDasboardAsHomepageEvent: fields["setDasboardAsHomepageEvent"] || false,
            setDasboardAsHomepageEventName: fields["setDasboardAsHomepageEventName"] || "make_dash_hmpg",
            returnRequestEvent: fields["returnRequestEvent"] || false,
            returnRequestEventName: fields["returnRequestEventName"] || "return_request",
            contactUsEvent: fields["contactUsEvent"] || false,
            requestQuoteEvent: fields["requestQuoteEvent"] || false,
        });
    }, [fields]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!cart) {
            return;
        }

        if (isCartPage && xGoogleAnalytics?.viewCartEvent) {
            setPDPInfoLoaded(false);
            setPLPInfoLoaded(false);

            const googleAnalyticsData = {
                event: xGoogleAnalytics?.viewCartEventName,
                ecommerce: {
                    currency: currencyCode,
                    value: cart.orderSubTotal,
                    items: cart?.cartLines?.map((cartItem, idx) => ({
                        item_id: cartItem.erpNumber,
                        item_name: cartItem.productName,
                        affiliation,
                        discount: cartItem.pricing
                            ? cartItem.pricing.unitNetPrice < cartItem.pricing.unitListPrice
                                ? cartItem.pricing.unitListPrice - cartItem.pricing.unitNetPrice
                                : 0
                            : 0,
                        price: cartItem.pricing?.unitNetPrice,
                        quantity: cartItem.qtyOrdered,
                        index: idx,
                        item_brand: xGoogleAnalytics["includeProductBrand"] ? cartItem.brand?.name || "" : undefined,
                        item_category: xGoogleAnalytics["includeProductCategory"]
                            ? cartItem.properties?.["category"] || ""
                            : undefined,
                        location_id: locationId,
                    })),
                },
            };

            dataLayer.push(googleAnalyticsData);
        }
    }, [currentPage, cart?.id, xGoogleAnalytics]);

    // While navigating between PDPs setPDPInfoLoaded to false in order to track other PDPs
    useEffect(() => {
        setPDPInfoLoaded(false);
    }, [PDPProduct?.productNumber]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer) {
            if (isPLP && PLPProducts?.length && !PLPInfoLoaded) {
                setPLPInfoLoaded(true);
                setPDPInfoLoaded(false);
                if (xGoogleAnalytics?.productImpressionEvent) {
                    const googleAnalyticsData = {
                        event: xGoogleAnalytics?.productImpressionEventName,
                        ecommerce: {
                            currencyCode,
                            impressions: PLPProducts.map((product, idx) => ({
                                name: product?.productTitle,
                                id: product?.id,
                                position: idx,
                            })),
                        },
                    };

                    dataLayer.push(googleAnalyticsData);
                }
            }

            if (
                isPDP &&
                (xGoogleAnalytics?.pdpViewEvent || xGoogleAnalytics?.productImpressionEvent) &&
                PDPProduct &&
                PDPProduct.pricing
            ) {
                setPDPInfoLoaded(true);
                setPLPInfoLoaded(false);
                if (xGoogleAnalytics?.pdpViewEvent) {
                    const googleAnalyticsData = {
                        event: xGoogleAnalytics.pdpViewEventName,
                        ecommerce: {
                            detail: {
                                currency: currencySymbol,
                                actionField: { list: "Product Detail Page", action: "detail" },
                                products: [
                                    {
                                        name: PDPProduct?.productTitle,
                                        id: PDPProduct?.id,
                                        price: PDPProduct.pricing?.unitListPriceDisplay || "",
                                        brand: xGoogleAnalytics["includeProductBrand"]
                                            ? PDPProduct.brand?.name || ""
                                            : undefined,
                                        category: xGoogleAnalytics["includeProductCategory"]
                                            ? PDPProduct.properties?.["category"] || ""
                                            : undefined,
                                        index: 0,
                                        discount: "",
                                        location: "",
                                    },
                                ],
                            },
                        },
                    };

                    const isDuplicatedEvent =
                        dataLayer[dataLayer.length - 1]?.event === xGoogleAnalytics?.pdpViewEventName &&
                        dataLayer[dataLayer.length - 1]?.ecommerce.detail.products[0].id === PDPProduct?.id;

                    if (!isDuplicatedEvent) {
                        dataLayer.push(googleAnalyticsData);
                    }
                }

                if (xGoogleAnalytics?.productImpressionEvent) {
                    const googleAnalyticsData = {
                        event: xGoogleAnalytics.productImpressionEventName,
                        ecommerce: {
                            currencyCode,
                            impressions: [
                                {
                                    name: PDPProduct?.productTitle,
                                    id: PDPProduct?.id,
                                    position: 0,
                                },
                            ],
                        },
                    };

                    dataLayer.push(googleAnalyticsData);
                }

                const item = hasProductInViewItemList(PDPProduct.id);
                if (item && xGoogleAnalytics.selectItemEvent) {
                    const googleAnalyticsData = {
                        event: xGoogleAnalytics.selectItemEventName,
                        ecommerce: {
                            item_list_id: xGoogleAnalytics["includeProductCategory"]
                                ? PDPProduct.properties?.["categoryId"] || ""
                                : undefined,
                            item_list_name: "Product Details Page",
                            affiliation,
                            items: [
                                {
                                    name: PDPProduct?.productTitle,
                                    id: PDPProduct?.productNumber,
                                    position: 0,
                                },
                            ],
                        },
                    };

                    dataLayer.push(googleAnalyticsData);
                }
            }
        }
    }, [PLPProducts?.length, PDPProduct?.productNumber, PDPProduct?.pricing, currentPage, xGoogleAnalytics]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer && xGoogleAnalytics?.shouldTrackPayment) {
            (window as any)._GaCheckoutFunnel(
                3,
                cart,
                currencyCode,
                affiliation,
                xGoogleAnalytics.includeProductBrand,
                xGoogleAnalytics.includeProductCategory,
            );
            resetShouldTrackPayment();
        }
    }, [xGoogleAnalytics?.shouldTrackPayment]);

    useEffect(() => {
        setAfilliation(affiliation);
    }, [affiliation]);

    useEffect(() => {
        const dataLayer = getDataLayer();
        if (dataLayer && xGoogleAnalytics?.shouldTrackNoStockSearch) {
            const nonStock = PLPProducts?.every(p => p.invntory?.qtyOnHand === 0);
            if (nonStock) {
                const googleAnalyticsData = {
                    event: xGoogleAnalytics.noStockSearchEventName,
                    search_term: query,
                    numResults: PLPProducts?.length,
                    affiliation,
                    user: userEmail,
                    items: PLPProducts?.map((product, idx) => ({
                        item_name: product?.productTitle,
                        item_id: product?.productNumber,
                        price: product?.pricing?.unitNetPrice,
                        index: idx,
                        item_brand: xGoogleAnalytics["includeProductBrand"] ? product?.brand?.name || "" : undefined,
                        item_category: xGoogleAnalytics["includeProductCategory"]
                            ? product?.properties?.["category"] || ""
                            : undefined,
                        item_list_id: xGoogleAnalytics["includeProductCategory"]
                            ? product?.properties?.["categoryId"] || ""
                            : undefined,
                    })),
                };

                dataLayer.push(googleAnalyticsData);
            }
            resetShouldTrackNoStockSearch();
        }
    }, [xGoogleAnalytics?.shouldTrackNoStockSearch]);

    // Function to check for "productImpression" events with a specific productNumber
    const hasProductInViewItemList = (id: string) => {
        const dataLayer = getDataLayer();
        if (dataLayer) {
            for (const event of dataLayer) {
                if (event.event === xGoogleAnalytics?.productImpressionEventName && event.ecommerce?.impressions) {
                    for (const item of event.ecommerce.impressions) {
                        if (item.id === id) {
                            return item;
                        }
                    }
                }
            }
        }
        return false;
    };

    return null;
};

const basicEventsTab = {
    displayName: "Basic Events",
    sortOrder: 1,
};
const customEventsTab = {
    displayName: "Custom Events",
    sortOrder: 2,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GoogleAnalyticsConnector),
    definition: {
        displayName: "Google Analytics Connector",
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.trackGoogleAnalyticsEvents,
                displayName: "Track GoogleAnalytics Events",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
            },
            {
                name: fields.addToCartEvent,
                displayName: "Add To Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.addToCartEventName,
                displayName: "Add To Cart (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "addToCart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.addToCartEvent,
            },
            {
                name: fields.removeFromCartLineEvent,
                displayName: "Remove Cartline (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.removeFromCartLineEventName,
                displayName: "Remove Cartline (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "remove_from_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.removeFromCartLineEvent,
            },
            {
                name: fields.pdpViewEvent,
                displayName: "PDP View (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpViewEventName,
                displayName: "PDP View (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_item",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.pdpViewEvent,
            },
            {
                name: fields.productImpressionEvent,
                displayName: "Product Impression (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.productImpressionEventName,
                displayName: "Product Impression (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "productImpression",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.productImpressionEvent,
            },
            {
                name: fields.selectItemEvent,
                displayName: "Select Item (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.selectItemEventName,
                displayName: "Select Item (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "select_item",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.selectItemEvent,
            },
            {
                name: fields.viewCartEvent,
                displayName: "View Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.viewCartEventName,
                displayName: "View Cart (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.viewCartEvent,
            },
            {
                name: fields.purchaseEvent,
                displayName: "Purchase (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.purchaseEventName,
                displayName: "Purchase (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "purchase",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.purchaseEvent,
            },
            {
                name: fields.selectPromotionEvent,
                displayName: "Select Promotion Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.selectPromotionEventName,
                displayName: "Select Promotion Event Name",
                editorTemplate: "TextField",
                defaultValue: "select-promotion",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.selectPromotionEvent,
            },
            {
                name: fields.searchEvent,
                displayName: "Search Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.searchEventName,
                displayName: "Search Event Name",
                editorTemplate: "TextField",
                defaultValue: "search",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.searchEvent,
            },
            {
                name: fields.noStockSearchEvent,
                displayName: "No Stock Search Event",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.noStockSearchEventName,
                displayName: "No Stock Search Event Name",
                editorTemplate: "TextField",
                defaultValue: "nostock_search",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.noStockSearchEvent,
            },
            {
                name: fields.loginEvent,
                displayName: "Login Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.loginEventName,
                displayName: "Login Event Name",
                editorTemplate: "TextField",
                defaultValue: "signin",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.loginEvent,
            },
            {
                name: fields.transactionCompleteEvent,
                displayName: "Transaction Complete (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.transactionCompleteEventName,
                displayName: "Transaction Complete (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "transactionComplete",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.transactionCompleteEvent,
            },
            {
                name: fields.requestQuoteEvent,
                displayName: "Quick RFQ Form, Cart RFQ Form, and Free-Form RFQ (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.contactUsEvent,
                displayName: "Contact Us (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.setDasboardAsHomepageEvent,
                displayName: "Make Dashboard My Homepage Event",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.setDasboardAsHomepageEventName,
                displayName: "Make Dashboard My Homepage Event Name",
                editorTemplate: "TextField",
                defaultValue: "make_dash_hmpg",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.setDasboardAsHomepageEvent,
            },
            {
                name: fields.returnRequestEvent,
                displayName: "Return Request Event",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.returnRequestEventName,
                displayName: "Return Request Event Name",
                editorTemplate: "TextField",
                defaultValue: "return_request",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.returnRequestEvent,
            },
            {
                name: fields.includeProductBrand,
                displayName: "Include Product Brand (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeProductCategory,
                displayName: "Include Product Category (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
        ],
    },
};

export default widgetModule;
